package com.upcominds.expedite.dto

import com.upcominds.expedite.model.Community
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.model.ChallengeType
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable

@Serializable
data class CommunityCompetitionCreateRequest(
    val name: String,
    val description: String = "",
    val imageUrl: String? = null,
    val imageFile: String? = null,
    val themeColor: String? = null,
    val startsAt: Instant,
    val endsAt: Instant,
    val participationWeight: Int = 40,
    val completionWeight: Int = 30,
    val performanceWeight: Int = 30
)

@Serializable
data class CompetitionCommunitySelectionRequest(
    val communityIds: List<String>
)

@Serializable
data class CompetitionScheduleUpdateRequest(
    val startsAt: Instant,
    val endsAt: Instant,
    val description: String? = null
)

@Serializable
data class CompetitionRepresentationSelectionRequest(
    val communityId: String
)

@Serializable
data class CompetitionRepresentationDTO(
    val competitionId: String,
    val selectedCommunityId: String,
    val confirmed: Boolean,
    val locked: Boolean,
    val lockedAt: String? = null,
    val options: List<Community> = emptyList()
)

@Serializable
data class CommunityCompetitionDTO(
    val id: String,
    val name: String,
    val description: String,
    val imageUrl: String? = null,
    val themeColor: String? = null,
    val startsAt: String,
    val endsAt: String,
    val status: String,
    val participationOpen: Boolean = false,
    val participationWeight: Int,
    val completionWeight: Int,
    val performanceWeight: Int,
    val createdBy: String,
    val activatedAt: String? = null,
    val completedAt: String? = null,
    val endedEarlyAt: String? = null,
    val eligibleMembersCount: Int = 0,
    val communities: List<Community> = emptyList(),
    val challenges: List<ChallengeDTO> = emptyList(),
    val canEditSchedule: Boolean = false
)

@Serializable
data class CompetitionCommunityAwardDTO(
    val communityId: String,
    val rank: Int,
    val title: String,
    val badgeName: String,
    val rewardDescription: String,
    val awardedAt: String
)

@Serializable
data class CompetitionCommunityScoreDTO(
    val rank: Int,
    val community: Community,
    val eligibleMembers: Int,
    val participants: Int,
    val completedParticipations: Int,
    val possibleCompletions: Int,
    val participationRate: Double,
    val completionRate: Double,
    val averagePerformance: Double,
    val totalPoints: Int,
    val score: Double,
    val award: CompetitionCommunityAwardDTO? = null
)

@Serializable
data class CompetitionUserScoreDTO(
    val rank: Int,
    val userId: String,
    val displayName: String,
    val communityId: String,
    val communityName: String,
    val challengesStarted: Int,
    val challengesCompleted: Int,
    val totalPoints: Int,
    val averagePerformance: Double
)

@Serializable
data class CompetitionChallengeUserScoreDTO(
    val rank: Int,
    val userId: String,
    val displayName: String,
    val communityId: String,
    val communityName: String,
    val status: String,
    val points: Int,
    val performance: Double
)

@Serializable
data class CompetitionChallengeLeaderboardDTO(
    val challengeId: String,
    val challengeName: String,
    val entries: List<CompetitionChallengeUserScoreDTO>,
    val communityStandings: List<CompetitionCommunityScoreDTO> = emptyList()
)

@Serializable
data class CommunityCompetitionLeaderboardDTO(
    val competitionId: String,
    val competitionName: String,
    val themeColor: String? = null,
    val status: String,
    val participationWeight: Int,
    val completionWeight: Int,
    val performanceWeight: Int,
    val currentUserId: String? = null,
    val currentUserCommunityId: String? = null,
    val communities: List<CompetitionCommunityScoreDTO> = emptyList(),
    val individuals: List<CompetitionUserScoreDTO> = emptyList(),
    val challenges: List<CompetitionChallengeLeaderboardDTO> = emptyList(),
    val awardsFinalized: Boolean = false
)

fun ChallengeDTO.toChallenge(): Challenge = Challenge(
    id = id,
    name = name,
    description = description,
    imageUrl = imageUrl,
    points = points,
    durationMinutes = durationMinutes,
    deadline = deadline,
    difficulty = Challenge.Difficulty.valueOf(difficulty),
    type = ChallengeType.valueOf(type),
    contentId = contentId,
    savingsConfig = savingsConfig,
    socialConfig = socialConfig,
    isCompleted = isCompleted,
    participationStatus = participationStatus,
    isCommunityCompetition = true,
    competitionId = competitionId
)
